import { AccountTree, TableChart, TableRows } from '@mui/icons-material';
import {
    Box,
    Checkbox,
    Chip,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type { IOrgUnitDetailDTO } from '@sharedInterfaces/IOrgUnit';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { CustomGraphQLError } from '@src/APIs/graphQl/graphQL';
import getOrgUnits from '@src/APIs/graphQl/OrgUnit/getOrgUnits';
import OrgUnitCard from '@src/App/Components/Cards/EntityCards/OrgUnits/ProjectCard/OrgUnitCard';
import { Container } from '@src/App/Components/Core/Layouts/LayoutElements/Container/Container';
import MvpSubNav, { type IMvpQuickAction, type IMvpRole } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
import OrgUnitOverlay from '@src/App/Components/Overlays/OrgUnitOverlay/OrgUnitOverlay';
import type { IDataTableField } from '@src/App/Components/Tables/DataTable/DataTable';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import { textContainsLowerCase } from '@src/Objects/Helper';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import EntitiesPage, { EViewType, type IEntitiesPageAction } from '../../Generic/EntitiesPage/EntitiesPage';
import type { IViewType } from '../../Generic/EntitiesPage/ViewTypeMenu';
import OrgUnitDiagram from '../OrgUnitDiagram/OrgUnitDiagram';

/**
 * Represents the Organization Units page.
 * @returns {JSX.Element}
 */
function OrgUnitsPage() {
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).orgUnit;
    const entities = useSelector((state: AppState) => state.orgUnits);
    const [activeRole, setActiveRole] = useState('hr');

    const [selected, setSelected] = useState<number[]>([]);
    const [loadingError, setLoadingError] = useState<CustomGraphQLError | null>(null);

    useEffect(() => {
        getOrgUnits().catch((ex) => {
            setLoadingError(ex);
        });
    }, []);

    const actions: IEntitiesPageAction[] = [];

    const tableFields: IDataTableField<any>[] = [
        { id: 'id', label: 'ID', align: 'left', disablePadding: false, type: 'string' },
        { id: 'title', label: langStrings.orgName, align: 'left', disablePadding: false, type: 'string' },
    ];

    const types: IViewType<IOrgUnitDetailDTO>[] = [
        {
            id: EViewType.ORGUNIT,
            title: langStrings.oftenUsed.orgUnits,
            icon: <AccountTree fontSize="small" />,
            renderContainer: (allEntities, innerEntries) => {
                return (
                    <Box sx={{ minHeight: 400 }}>
                        <OrgUnitDiagram units={innerEntries} />
                    </Box>
                );
            },
        },
        {
            id: EViewType.TABLE,
            title: langStrings.oftenUsed.table,
            icon: <TableRows fontSize="small" />,
            minWidth: 375,
            renderContainer: (allEntities, innerEntries, width, selectedItems, tableSettings) => {
                if (!tableSettings) return <div>Loading...</div>;

                const hiddenFieldsSet = new Set(
                    Object.keys(tableSettings.hiddenFields).filter((key) => tableSettings.hiddenFields[key]),
                );
                const visibleFields = tableFields.filter((f) => !hiddenFieldsSet.has(f.id));
                const sortedFields = visibleFields.sort((a, b) => {
                    const orderA = tableSettings.order.indexOf(a.id);
                    const orderB = tableSettings.order.indexOf(b.id);
                    return orderA - orderB;
                });

                return (
                    <Box sx={{ width: '100%' }}>
                        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <TableContainer>
                                <Table sx={{ minWidth: 750 }}>
                                    <TableHead
                                        sx={{
                                            bgcolor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? alpha(theme.palette.primary.light, 0.15)
                                                    : alpha(theme.palette.primary.main, 0.12),
                                        }}
                                    >
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    sx={{
                                                        color: (theme) =>
                                                            theme.palette.mode === 'dark'
                                                                ? theme.palette.primary.light
                                                                : theme.palette.primary.dark,
                                                        '&.Mui-checked': {
                                                            color: (theme) =>
                                                                theme.palette.mode === 'dark'
                                                                    ? theme.palette.primary.light
                                                                    : theme.palette.primary.dark,
                                                        },
                                                    }}
                                                    indeterminate={
                                                        selected.length > 0 && selected.length < innerEntries.length
                                                    }
                                                    checked={
                                                        innerEntries.length > 0 &&
                                                        selected.length === innerEntries.length
                                                    }
                                                    onChange={(event) => {
                                                        if (event.target.checked) {
                                                            setSelected(innerEntries.map((n) => n.id));
                                                        } else {
                                                            setSelected([]);
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            {sortedFields.map((field) => (
                                                <TableCell
                                                    key={field.id}
                                                    sx={{
                                                        fontWeight: 500,
                                                        color: (theme) =>
                                                            theme.palette.mode === 'dark'
                                                                ? theme.palette.primary.light
                                                                : theme.palette.primary.dark,
                                                    }}
                                                >
                                                    {field.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {innerEntries.map((entity) => {
                                            const isItemSelected = selected.includes(entity.id);
                                            return (
                                                <TableRow
                                                    hover
                                                    onClick={() => {
                                                        const selectedIndex = selected.indexOf(entity.id);
                                                        let newSelected: number[] = [];
                                                        if (selectedIndex === -1) {
                                                            newSelected = newSelected.concat(selected, entity.id);
                                                        } else if (selectedIndex === 0) {
                                                            newSelected = newSelected.concat(selected.slice(1));
                                                        } else if (selectedIndex === selected.length - 1) {
                                                            newSelected = newSelected.concat(selected.slice(0, -1));
                                                        } else if (selectedIndex > 0) {
                                                            newSelected = newSelected.concat(
                                                                selected.slice(0, selectedIndex),
                                                                selected.slice(selectedIndex + 1),
                                                            );
                                                        }
                                                        setSelected(newSelected);
                                                    }}
                                                    role="checkbox"
                                                    aria-checked={isItemSelected}
                                                    tabIndex={-1}
                                                    key={entity.id}
                                                    selected={isItemSelected}
                                                    sx={{ cursor: 'pointer' }}
                                                >
                                                    <TableCell padding="checkbox">
                                                        <Checkbox color="primary" checked={isItemSelected} />
                                                    </TableCell>
                                                    {sortedFields.map((field) => (
                                                        <TableCell key={field.id}>
                                                            {field.id === 'id' && entity.id}
                                                            {field.id === 'title' && (
                                                                <OrgUnitOverlay id={entity.id}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ fontWeight: 500, cursor: 'pointer' }}
                                                                    >
                                                                        {entity.title}
                                                                    </Typography>
                                                                </OrgUnitOverlay>
                                                            )}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Box>
                );
            },
        },
        {
            id: EViewType.CARD,
            title: langStrings.oftenUsed.cards,
            icon: <TableChart fontSize="small" />,
            renderContainer: (allEntities, innerEntries) => (
                <Container>
                    {innerEntries.map((entity) => {
                        const isSelected = selected.includes(entity.id);
                        return (
                            <OrgUnitCard
                                key={entity.id}
                                entity={entity}
                                isSelected={isSelected}
                                showCheckbox={actions.length > 0}
                                onSelect={(id) => {
                                    const newSelected = isSelected
                                        ? selected.filter((selectedId) => selectedId !== id)
                                        : [...selected, id];
                                    setSelected(newSelected);
                                }}
                            />
                        );
                    })}
                </Container>
            ),
        },
    ];

    const renderOrgUnitStatsChips = () => {
        const totalEmployees = entities?.reduce((sum, ou) => sum + (ou.employees?.length || 0), 0) || 0;
        const activeOUs = entities?.filter((ou) => ou.employees && ou.employees.length > 0).length || 0;
        const avgEmployeesPerOU = entities && entities.length > 0 ? Math.round(totalEmployees / entities.length) : 0;

        return (
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                <Chip
                    label={`${entities?.length || 0} Organisationseinheiten`}
                    color="primary"
                    variant="outlined"
                    size="small"
                />
                <Chip label={`${totalEmployees} Mitarbeiter gesamt`} color="primary" variant="outlined" size="small" />
                <Chip label={`${activeOUs} aktive OUs`} color="primary" variant="outlined" size="small" />
                <Chip label={`∅ ${avgEmployeesPerOU} MA/OU`} color="primary" variant="outlined" size="small" />
            </Stack>
        );
    };

    const roles: IMvpRole[] = [
        {
            id: 'hr',
            label: 'HR',
            icon: getAppIcon(EIconCategory.EMPLOYEES),
        },
        {
            id: 'admin',
            label: 'Admin',
            icon: getAppIcon(EIconCategory.SETTINGS),
        },
    ];

    const quickActions: IMvpQuickAction[] = [
        {
            id: 'company-overview',
            label: 'Übersicht',
            icon: getAppIcon(EIconCategory.OVERVIEW),
            onClick: () => navigate(`/${ELinks.COMPANY}`),
            variant: 'primary',
        },
        {
            id: 'employees',
            label: 'Mitarbeitende',
            icon: getAppIcon(EIconCategory.EMPLOYEES),
            onClick: () => navigate(`/${ELinks.PEOPLE_EMPLOYEES}`),
        },
    ];

    return (
        <>
            <MvpSubNav
                currentPageTitle="Abteilungsbereiche"
                roles={roles}
                activeRole={activeRole}
                onRoleChange={setActiveRole}
                quickActions={quickActions}
            />
            <EntitiesPage
                title="Abteilungsbereiche"
                entities={entities}
                selected={selected}
                loadingError={loadingError}
                tableFields={tableFields}
                tableId="org-units-table"
                filter={(entity, selectedView, searchText) =>
                    selectedView.filter(entity) && textContainsLowerCase(searchText, entity.title)
                }
                setSelected={setSelected}
                views={[
                    {
                        id: 'all',
                        title: langStrings.oftenUsed.allEntries,
                        filter() {
                            return true;
                        },
                    },
                ]}
                types={types}
                actions={actions}
                chips={renderOrgUnitStatsChips()}
            />
        </>
    );
}
export default OrgUnitsPage;
